import { defineStore } from 'pinia'
import { $userAvailableColumns, type DivisionDefinition } from '@/api/division-settings'
import {
  COLUMN_TYPE_BRANDING,
  COLUMN_TYPE_KOP,
  COLUMN_TYPE_WAYFINDING,
  filterHeadersByAllowedColumnKeys,
  type ColumnType,
  type TableHeader,
} from '@/store/helpers/default-values'

export type DivisionColumnsState = {
  isLoading: boolean
  isLoaded: boolean
  loadedColumnTypes: ColumnType[]
  divisions: DivisionDefinition[]
  columnKeys: Partial<Record<ColumnType, string[]>>
}

export const useDivisionColumnsStore = defineStore({
  id: 'divisionColumns',
  persist: false,
  state: (): DivisionColumnsState => ({
    isLoading: false,
    isLoaded: false,
    loadedColumnTypes: [],
    divisions: [],
    columnKeys: {},
  }),

  actions: {
    async fetchUserAvailableColumns (columnTypes?: ColumnType[], force = false) {
      const requestedColumnTypes = columnTypes ?? [
        COLUMN_TYPE_BRANDING,
        COLUMN_TYPE_WAYFINDING,
        COLUMN_TYPE_KOP,
      ]

      if (!force && requestedColumnTypes.every(columnType => this.loadedColumnTypes.includes(columnType)))
        return

      if (this.isLoading)
        return

      this.isLoading = true

      try {
        const data = await $userAvailableColumns(columnTypes)

        this.divisions = data.divisions
        this.columnKeys = {
          ...this.columnKeys,
          ...data.column_keys,
        }
        this.loadedColumnTypes = [
          ...new Set([
            ...this.loadedColumnTypes,
            ...Object.keys(data.column_keys),
          ]),
        ] as ColumnType[]
        this.isLoaded = true
      }
      finally {
        this.isLoading = false
      }
    },

    async fetchAllUserAvailableColumns (force = false) {
      await this.fetchUserAvailableColumns([
        COLUMN_TYPE_BRANDING,
        COLUMN_TYPE_WAYFINDING,
        COLUMN_TYPE_KOP,
      ], force)
    },

    getAllowedColumnKeys (columnType: ColumnType) {
      return this.columnKeys[columnType]
    },

    restrictHeaders (headers: TableHeader[], columnType: ColumnType) {
      return filterHeadersByAllowedColumnKeys(
        headers,
        this.getAllowedColumnKeys(columnType),
      )
    },
  },
})

export type DivisionColumnsStore = ReturnType<typeof useDivisionColumnsStore>
